import type { FC } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd/es/form";

import { Button, Form, Input } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import {
  emptyDbConfigRow,
  emptyGameStopRow,
  emptyWorkloadRow,
  type RuleFormValues,
} from "./ruleFormData";

import styles from "./Compliance.module.css";

type RegulatorPortalRuleFormProps = {
  form: FormInstance<RuleFormValues>;
  initialValues: RuleFormValues;
  onFinish: (values: RuleFormValues) => void;
};

const RegulatorPortalRuleForm: FC<RegulatorPortalRuleFormProps> = ({
  form,
  initialValues,
  onFinish,
}) => (
  <Form<RuleFormValues>
    form={form}
    initialValues={initialValues}
    onFinish={onFinish}
    className={styles.form}
    data-testid="regulator-portal-rule-form"
  >
    <div className={styles.details}>
      <div className={styles.formRow}>
        <Form.Item
          name="description"
          label={AppStrings.DESCRIPTION}
        >
          <Input data-testid="regulator-portal-rule-description-input" />
        </Form.Item>
      </div>
      <div className={styles.formRow}>
        <Form.Item
          name="targetAccountId"
          label={AppStrings.PAM_REGULATOR_PORTAL_TARGET_ACCOUNT}
          extra={AppStrings.PAM_REGULATOR_PORTAL_RULE_ACCOUNT_LOCKED}
        >
          <Input
            disabled
            data-testid="regulator-portal-rule-target-input"
          />
        </Form.Item>
        <Form.Item
          name="baselineAccountId"
          label={AppStrings.PAM_REGULATOR_PORTAL_BASELINE_ACCOUNT}
          extra={AppStrings.PAM_REGULATOR_PORTAL_RULE_ACCOUNT_LOCKED}
        >
          <Input
            disabled
            data-testid="regulator-portal-rule-baseline-input"
          />
        </Form.Item>
      </div>
    </div>

    <Form.List name="monitoredWorkloads">
      {(fields, { add, remove }) => (
        <div className={styles.detailSection}>
          <div className={styles.detailSectionHeader}>
            <h2 className={styles.detailSectionTitle}>
              {formatString(AppStrings.PAM_REGULATOR_PORTAL_MONITORED_WORKLOADS_TITLE, {
                count: String(fields.length),
              })}
            </h2>
            <Button
              htmlType="button"
              intent="neutral"
              variant="outline"
              iconLeft={<PlusOutlined />}
              onClick={() => add(emptyWorkloadRow())}
              data-testid="regulator-portal-rule-add-workload"
            >
              {AppStrings.PAM_REGULATOR_PORTAL_ADD_WORKLOAD}
            </Button>
          </div>
          {fields.length === 0 ? (
            <p className={styles.message}>
              {AppStrings.PAM_REGULATOR_PORTAL_WORKLOADS_EMPTY}
            </p>
          ) : (
            fields.map((field, index) => (
              <div
                key={field.key}
                className={styles.editorRow}
                data-testid={`regulator-portal-rule-workload-row-${index}`}
              >
                <Form.Item
                  name={[field.name, "resourceType"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_RESOURCE_TYPE}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "region"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_REGION}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "clusterName"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_CLUSTER}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "namespace"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_NAMESPACE}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "deployment"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_DEPLOYMENT}
                >
                  <Input />
                </Form.Item>
                <Button
                  htmlType="button"
                  intent="danger"
                  variant="ghost"
                  iconLeft={<DeleteOutlined />}
                  aria-label={AppStrings.PAM_REGULATOR_PORTAL_REMOVE_WORKLOAD}
                  onClick={() => remove(field.name)}
                />
              </div>
            ))
          )}
        </div>
      )}
    </Form.List>

    <Form.List name="gameStops">
      {(fields, { add, remove }) => (
        <div className={styles.detailSection}>
          <div className={styles.detailSectionHeader}>
            <h2 className={styles.detailSectionTitle}>
              {formatString(AppStrings.PAM_REGULATOR_PORTAL_GAME_STOPS_TITLE, {
                count: String(fields.length),
              })}
            </h2>
            <Button
              htmlType="button"
              intent="neutral"
              variant="outline"
              iconLeft={<PlusOutlined />}
              onClick={() => add(emptyGameStopRow())}
              data-testid="regulator-portal-rule-add-game-stop"
            >
              {AppStrings.PAM_REGULATOR_PORTAL_ADD_GAME_STOP}
            </Button>
          </div>
          {fields.length === 0 ? (
            <p className={styles.message}>
              {AppStrings.PAM_REGULATOR_PORTAL_GAME_STOPS_EMPTY}
            </p>
          ) : (
            fields.map((field, index) => (
              <div
                key={field.key}
                className={styles.editorRowWide}
                data-testid={`regulator-portal-rule-game-stop-row-${index}`}
              >
                <Form.Item
                  name={[field.name, "name"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_GAME_STOP_NAME}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "resources"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_RESOURCES}
                  extra={AppStrings.PAM_REGULATOR_PORTAL_RESOURCES_HINT}
                >
                  <Input />
                </Form.Item>
                <Button
                  htmlType="button"
                  intent="danger"
                  variant="ghost"
                  iconLeft={<DeleteOutlined />}
                  aria-label={AppStrings.PAM_REGULATOR_PORTAL_REMOVE_GAME_STOP}
                  onClick={() => remove(field.name)}
                />
              </div>
            ))
          )}
        </div>
      )}
    </Form.List>

    <Form.List name="monitoredDbConfigs">
      {(fields, { add, remove }) => (
        <div className={styles.detailSection}>
          <div className={styles.detailSectionHeader}>
            <h2 className={styles.detailSectionTitle}>
              {formatString(AppStrings.PAM_REGULATOR_PORTAL_MONITORED_DB_CONFIGS_TITLE, {
                count: String(fields.length),
              })}
            </h2>
            <Button
              htmlType="button"
              intent="neutral"
              variant="outline"
              iconLeft={<PlusOutlined />}
              onClick={() => add(emptyDbConfigRow())}
              data-testid="regulator-portal-rule-add-db-config"
            >
              {AppStrings.PAM_REGULATOR_PORTAL_ADD_DB_CONFIG}
            </Button>
          </div>
          {fields.length === 0 ? (
            <p className={styles.message}>
              {AppStrings.PAM_REGULATOR_PORTAL_DB_CONFIGS_EMPTY}
            </p>
          ) : (
            fields.map((field, index) => (
              <div
                key={field.key}
                className={styles.editorRow}
                data-testid={`regulator-portal-rule-db-config-row-${index}`}
              >
                <Form.Item
                  name={[field.name, "tableName"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_TABLE}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "region"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_REGION}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "fields"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_FIELDS}
                  extra={AppStrings.PAM_REGULATOR_PORTAL_FIELDS_HINT}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, "conditions"]}
                  label={AppStrings.PAM_REGULATOR_PORTAL_CONDITIONS}
                  extra={AppStrings.PAM_REGULATOR_PORTAL_CONDITIONS_HINT}
                >
                  <Input />
                </Form.Item>
                <Button
                  htmlType="button"
                  intent="danger"
                  variant="ghost"
                  iconLeft={<DeleteOutlined />}
                  aria-label={AppStrings.PAM_REGULATOR_PORTAL_REMOVE_DB_CONFIG}
                  onClick={() => remove(field.name)}
                />
              </div>
            ))
          )}
        </div>
      )}
    </Form.List>
  </Form>
);

export default RegulatorPortalRuleForm;
